import {
	Autocomplete,
	Box,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import './AIEmailGenerator.scss';
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown';
import Input from '../../../components/Input/Input';
import EmailContent from './EmailContent/EmailContent';
import EmailBody from './EmailTemplate/EmailBody/EmailBody';
import ToolTipOnText from '../../../components/Tooltip/Tooltip';
import useApiService from '../../../services/api.service';
import AIEmailGeneratorEndpoints from './AIEmailGenerator.endpoints';
import useToast from '../../../components/Toast/hooks/useToast';
import { useAIEmailContext } from './AIEmailContext';
import {
	debounce,
	personalizationOptions,
	transformObjectives,
} from './AIEmailGenerator.data';
import {
	hideLoader,
	showLoader,
} from '../../../components/CustomLoader/CustomLoader';
import CustomAutocompleteWithAddNew from '../../../components/CustomAutocompleteWithAddNew/CustomAutocompleteWithAddNew';
import { useEnrichType } from '../../DataEnrichment/DataEnrichmentContext';
import { useAppContext } from '../../../AppContext/App.context';
import { AppUtilityFunctions } from '../../../utils/appUtils';

const AIEmailGenerator = () => {
	const theme = useTheme();
	const APIService = useApiService();
	const toaster = useToast();
	const [addingNew, setAddingnew] = useState(false);
	const skipObjectiveSuggestionRef = useRef(false);

	const {
		emailData,
		showEmailBody,
		emailNameOptions,
		suggestedEmailName,
		setEmailNameOptions,
		setEmailData,
		setSuggestedEmailName,
	} = useAIEmailContext();
	const { segment, setSegment } = useEnrichType();
	// const [segments, setSegment] = useState<string>('');
	const [emailType, setEmailType] = useState<number>(1);
	const [category, setCategory] = useState<string>('');
	const [emailSeq, setEmailSeq] = useState<{
		label: string;
		value: string;
	} | null>(null);

	const [segments, setSegments] = useState<any>();
	const [objectives, setObjectives] = useState<any[]>([]);
	const [senderName, setSenderName] = useState('');
	const [emailName, setEmailName] = useState<any>('');
	const [appData]: any = useAppContext();
	const { userData } = appData;
	const { user_id } = userData;

	const skipEmailLogFetchRef = useRef(false);
	const isLightTheme = theme.palette.mode === 'light';

	const isEmailGenerated = Boolean(emailNameOptions?.length);

	useEffect(() => {
		if (skipEmailLogFetchRef.current) {
			skipEmailLogFetchRef.current = false;
			return;
		}
		if (emailName?.trim() && addingNew === false) {
			getEmailLogs(segment);
		}
	}, [emailName]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const segmentResponse = await fetchSegment();
				if (segmentResponse.segment_list.length > 0) {
					const emailNamesResponse = await fetchEmailNames(
						segmentResponse.latest_segment
					);
					if (emailNamesResponse) {
						if (emailName) {
							await getEmailLogs(segment);
						}
					}
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};
		fetchData();
	}, []);

	useEffect(() => {
		if (!emailData) return;

		(async () => {
			setCategory(emailData.data?.email_criteria ?? '');

			const objective = emailData.data?.objective;
			const input = emailData.data?.input;

			if (objective && input) {
				skipObjectiveSuggestionRef.current = true;
				setEmailSeq({
					label: objective,
					value: input,
				});
			} else {
				setEmailSeq(null);
			}

			setSenderName(emailData?.data?.sender_name ?? '');
			setEmailType(emailData?.data?.email_type ?? emailType);

			if (emailData.data?.segment) {
				// setSegment(emailData.data?.segment);
			}

			setEmailName(emailData?.data?.email_name ?? suggestedEmailName);
			sendObjectives(emailData?.data?.email_criteria);
		})();
	}, [emailData]);

	const getEmailLogs = async (segment) => {
		const request = {
			segment,
			email_name: emailName,
		};
		showLoader();
		try {
			const res = await APIService.get(
				AIEmailGeneratorEndpoints.getEmailLogs(request),
				true
			);
			setEmailData(res);
		} catch (err) {
			console.error(err);
		} finally {
			hideLoader();
		}
	};

	const fetchSegment = async () => {
		showLoader();
		try {
			const res: any = await APIService.get(
				AIEmailGeneratorEndpoints.fetchSegment({}),
				true
			);
			if (res.data) {
				const segmentList = res.data.segment_list.map((item) => item.label);
				setSegments(segmentList);
				if (res.data.latest_segment) {
					// setSegment(res.data.latest_segment);
				}
			}
			hideLoader();
			return res.data;
		} catch (err) {
			hideLoader();
			toaster.addToast({
				message: 'Segment fetch failed',
				timeout: 3000,
				type: 'error',
			});
			console.error('Segment fetch failed:', err);
		}
	};

	const fetchEmailNames = async (segment) => {
		const reqBody = {
			segment: segment,
		};
		showLoader();
		try {
			const res: any = await APIService.get(
				AIEmailGeneratorEndpoints.getEmailNames(reqBody),
				true
			);
			if (res) {
				setEmailNameOptions(res.data.existing_draft_names);
				setSuggestedEmailName(res.data.next_suggested_draft);
				if (
					res?.message !== 'Success' &&
					user_id !== 989213 &&
					!AppUtilityFunctions.isFreemiumUser()
				) {
					toaster.addToast({
						message: res.message,
						timeout: 4000,
						type: 'warning',
					});
				}
				if (res.data.existing_draft_names.length > 0) {
					setEmailName(res.data.existing_draft_names[0]);
					// displaying latest email draft
				} else {
					setEmailName(res.data.next_suggested_draft);
				}
			}
			hideLoader();
			return res.data.existing_draft_names;
		} catch (err) {
			hideLoader();
			console.error('Segment fetch failed:', err);
		}
	};

	const getObjectiveSuggestions = useCallback(async (search: string) => {
		if (!addingNew && search) {
			try {
				const res = await APIService.post(
					AIEmailGeneratorEndpoints.getObjectiveSuggestions({
						reqBody: { search },
					}),
					true
				);
				setObjectives(res.data);
			} catch (err) {
				toaster.addToast({
					message: 'Something went wrong',
					timeout: 3000,
					type: 'error',
				});
			}
		}
	}, []);

	const debouncedFetch = useMemo(
		() => debounce(getObjectiveSuggestions, 500),
		[]
	);

	const sendObjectives = async (selected: string) => {
		if (selected) {
			try {
				const res = await APIService.post(
					AIEmailGeneratorEndpoints.sendObjectives({
						reqBody: { email_criteria: selected },
					}),
					true
				);
				setObjectives(res.data);
			} catch (err) {
				toaster.addToast({
					message: 'Something went wrong',
					timeout: 3000,
					type: 'error',
				});
			}
		}
	};

	const handleEmailTypeChange = (event) => {
		const newEmailType = Number(event.target.value);
		setEmailType(newEmailType);

		if (!addingNew) {
			setEmailData((prev) => ({
				data: {
					sender_name: senderName,
					segment: prev?.data?.segment ?? segment,
					email_name: prev?.data?.email_name ?? emailName,
				},
			}));
			setObjectives([]);
		}
	};

	// const handleEmailTypeChange = (event) => {
	// 	const newEmailType = Number(event.target.value);
	// 	setEmailType(newEmailType);
	// 	if (!addingNew) {
	// 		setEmailData({});
	// 		setObjectives([]);
	// 	}
	// };

	const onSegmentChange = async (e) => {
		setSegment(e as string);
		try {
			const emailNamesResponse = await fetchEmailNames(e);
			// if (emailNamesResponse.length > 0) {
			if (emailName) {
				await getEmailLogs(e);
			}
			// }
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	return (
		<Box>
			<Box className='container' bgcolor={isLightTheme ? '#F4F7FC' : '#1A1A1A'}>
				<Box
					className='innerContainer'
					bgcolor={isLightTheme ? '#fff' : '#000'}
				>
					{/* Top Form Section */}
					<Box display='flex' flexDirection='row' alignItems='center'>
						<FormLabel
							className='select'
							style={{ color: theme.palette.text.primary }}
						>
							Select Segment *
						</FormLabel>
						<CustomDropdown
							options={segments || []}
							placeholder='Select Segment'
							value={segment}
							className='select-segment'
							onChange={(e) => onSegmentChange(e)}
						/>
						<FormLabel
							className='select'
							style={{ color: theme.palette.text.primary, marginLeft: '38px' }}
						>
							Email Name *
						</FormLabel>
						<CustomAutocompleteWithAddNew
							width={300}
							pointerEvents={!segments?.length ? 'none' : ''}
							onAddNewOption={() => {
								setAddingnew(true);
								skipEmailLogFetchRef.current = true;
								setEmailData({});
								setEmailName(suggestedEmailName);
							}}
							className='email-name'
							options={emailNameOptions || []}
							value={emailName}
							onChange={(newValue, reason) => {
								if (reason === 'selectOption') {
									setEmailName(newValue);
									setAddingnew(false);
								} else {
									skipEmailLogFetchRef.current = true;
									setEmailName(newValue);
								}
							}}
							placeholder='Select or type email name'
						/>
					</Box>
					{/* Personalization Radio */}
					<Box mt={2.7} display='flex' alignItems='center'>
						<FormLabel
							className='select'
							style={{ color: theme.palette.text.primary }}
						>
							Provide the Name of the person sending the email *
						</FormLabel>
						<Input
							name='name'
							value={senderName}
							placeholder='Enter Name'
							onChange={(e) => setSenderName(e.target.value)}
							containerClass='name-input'
						/>
					</Box>
					{/* Personalization Radio */}
					<Box mt={2.7} display='flex' alignItems='center'>
						<FormLabel
							style={{ color: theme.palette.text.primary, marginRight: '38px' }}
						>
							Select your personalization preference
						</FormLabel>
						<RadioGroup
							row
							name='personalization-type'
							value={emailType}
							onChange={handleEmailTypeChange}
						>
							{personalizationOptions.map((option) => (
								<Box key={option.value} display='flex' marginRight={2}>
									<FormControlLabel
										value={option.value}
										control={<Radio />}
										label={option.label}
										style={{ marginRight: 0 }}
									/>
									<ToolTipOnText title={option.tooltip}>
										<InfoOutlinedIcon
											style={{
												color: isLightTheme ? 'black' : 'white',
												margin: '10px 0 0 5px',
											}}
											fontSize='small'
										/>
									</ToolTipOnText>
								</Box>
							))}
						</RadioGroup>
					</Box>

					{/* Category & Email Sequence */}
					<Box mt={2.7} display='flex' alignItems='center'>
						<FormLabel
							className='select'
							style={{ color: theme.palette.text.primary }}
						>
							Select Category
						</FormLabel>
						<CustomDropdown
							optionWidth={205}
							className='category'
							showCross={true}
							options={['Marketing Nurture', 'Sales Emails']}
							value={category}
							onChange={(val) => {
								setCategory(val as string);
								setEmailSeq(null);
								sendObjectives(val as string);
							}}
						/>

						<FormLabel
							className='select-seq'
							style={{ color: theme.palette.text.primary }}
						>
							Select Email Sequence Type
						</FormLabel>

						<Autocomplete
							ListboxProps={{
								className: 'email-seg-listbox',
							}}
							disablePortal
							className='email-seg'
							value={emailSeq ?? null}
							onKeyPress={(event: any) => {
								if (skipObjectiveSuggestionRef.current) {
									skipObjectiveSuggestionRef.current = false;
									return;
								}
								// debouncedFetch(event.target.value);
							}}
							onChange={(event, newValue) => setEmailSeq(newValue || null)}
							options={transformObjectives(objectives)}
							getOptionLabel={(option: any) => option.label}
							isOptionEqualToValue={(option, value) =>
								option.label === value.label
							}
							sx={{ width: 350 }}
							renderOption={(props, option) => {
								const shouldShowTooltip = option.label.length > 57;

								return (
									<li {...props}>
										<ToolTipOnText
											title={shouldShowTooltip ? option.label : ''}
											placement='bottom'
										>
											<Typography
												sx={{
													overflow: 'hidden',
													whiteSpace: 'nowrap',
													textOverflow: 'ellipsis',
													width: 450,
												}}
											>
												{/* <span style={{ marginRight: 8, color: '#9149ff' }}>
													•
												</span> */}
												{option.label}
											</Typography>
										</ToolTipOnText>
									</li>
								);
							}}
							renderInput={(params) => (
								<TextField {...params} placeholder='Select Sequence Type' />
							)}
						/>
					</Box>
				</Box>
			</Box>

			{/* Email Preview Section */}
			<Box display='flex' gap={10} mt={5}>
				<Box width='50%'>
					<EmailContent
						isEmailGenerated={isEmailGenerated}
						emailType={emailType}
						segment={segment}
						emailSeq={emailSeq}
						senderName={senderName}
						category={category}
						emailName={emailName}
						addingNew={addingNew}
					/>
				</Box>
				{(emailData || showEmailBody) && (
					<Box width='50%'>
						<EmailBody
							selectedValue={emailType}
							segment={segment}
							emailName={emailName}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default AIEmailGenerator;
